<template>
  <b-container id="CreditCardForm">
    <b-row>
      <b-col class="mainTitle text-center">
        <h3>Credit Card Info</h3>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="5" sm="12" offset="1" class="mb-5 mb-md-0">
        <template v-if="planInformation">
          <h4 class="title">
            <template v-if="planInformation.length === 1">Monthly</template>
            <template v-else-if="planInformation.length === 6">6 Months</template>
            <template v-else-if="planInformation.length === 12">Annually</template>
          </h4>
          <h1 class="price">{{ formatCurrency(this.planInformation.amount) }}</h1>
        </template>
      </b-col>
      <b-col md="5" sm="12">
        <form>
          <b-row class="inputField">
            <b-col lg="6" sm="12">
              <div class="form-group">
                <div class="input-wrapper">
                  <div id="creditCardNumber" class="hosted-field"></div>
                  <label class="hosted-field--label" for="creditCardNumber"
                    >Credit card number</label
                  >
                  <b-form-invalid-feedback>
                    Check your credit card number
                  </b-form-invalid-feedback>
                </div>
              </div>
            </b-col>
            <b-col lg="6" sm="12">
              <div class="form-group">
                <div class="input-wrapper">
                  <div id="cardHolderName" class="hosted-field"></div>
                  <label class="hosted-field--label" for="cardHolderName"
                    >Card holder name</label
                  >
                  <b-form-invalid-feedback>
                    Card holder name is required and only accepts alphabetic characters (no number or special characters are allowed)
                  </b-form-invalid-feedback>
                </div>
              </div>
            </b-col>

            <b-col lg="6" sm="12">
              <div class="form-group">
                <div class="input-wrapper">
                  <div id="expireDate" class="hosted-field"></div>
                  <label class="hosted-field--label" for="expireDate"
                    >Expire date</label
                  >
                  <b-form-invalid-feedback>
                    Expiration date required
                  </b-form-invalid-feedback>
                </div>
              </div>
            </b-col>

            <b-col lg="6" sm="12">
              <div class="form-group">
                <div class="input-wrapper">
                  <div id="cvvInput" class="hosted-field"></div>
                  <label class="hosted-field--label" for="cvvInput">CVV</label>
                  <b-form-invalid-feedback>
                    Security code required
                  </b-form-invalid-feedback>
                </div>
                <div class="hint">3-4 digits code</div>
              </div>
            </b-col>
            <b-col cols="12">
              <div class="hint">
                <p>
                  By clicking the Create My Account button, you acknowledge that
                  you have read and agree to
                    <span>
                      TorchMetrics™
                      <router-link :to="{ name: 'PrivacyPolicy' }">
                        Privacy Policy
                      </router-link>
                      and
                      <router-link :to="{ name: 'TermsOfUse' }">
                        Terms of Use
                      </router-link>
                    </span>
                </p>
              </div>
            </b-col>
          </b-row>
        </form>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { mapActions, mapState } from "vuex";
import ShowPassword from "@/components/common/ShowPassword";
import braintree from "braintree-web";
import { normalizeUserData } from "@/services/utils";
import Api from "@/services/api";
import { getPlanAmountFromState, currencyFormatter, changeToInitialCaps } from "@/services/utils";
import { getPlanIdFromState } from "@/services/utils";

export default {
  name: "SignUpForm",
  components: { ShowPassword },
  props: ["code", "planInformation", "isForOrganization"],
  data() {
    return {
      paymentInfo: {
        full_name: null,
        email: null,
        nonce: null,
        plan_id: null,
      },
      registrationInfo: {
        full_name: null,
        email: null,
        admin: false,
        braintree_customer_id: null,
        next_payment: true,
        cancelled_recurring: false,
        plan_id: null,
        organization_id: null,
        last_braintree_transaction_id: null,
        type: 'Presenter',
      },
      hostedFieldInstance: false,
      braintreeError: "",
    };
  },
  computed: {
    ...mapState(["currentUser"]),
    hasBraintreeError() {
      return this.braintreeError != "";
    },
  },
  methods: {
    ...mapActions([
      "REGISTER",
      "REGISTER_ORG",
      "LOG_IN",
      "SET_PAYMENT_API_ERRORS",
    ]),
    payAndSignUp(full_name, email, password, company = null, phone = null) {
      this.paymentInfo.nonce = null;
      return new Promise((resolve, reject) => {
        if (this.hostedFieldInstance) {
          // Braintree data tokenization.
          this.hostedFieldInstance
            .tokenize()
            .then((payload) => {
              this.braintreeError = "";
              // Prepare data to make a payment (braintree transaction)
              this.paymentInfo.full_name = changeToInitialCaps(full_name);
              this.paymentInfo.email = email;
              this.paymentInfo.nonce = payload.nonce;
              this.paymentInfo.plan_id = this.planInformation.id;

              Api()
                .post("pay", this.paymentInfo)
                .then((paymentResponse) => {
                  console.log('paymentResponse', paymentResponse)
                  // Payment process had errors.
                  if (paymentResponse.data.errors) {
                    this.SET_PAYMENT_API_ERRORS(paymentResponse.data.message)
                    reject(paymentResponse.data.message);
                  }
                  // Successful payment.
                  else {
                    let registrationInfo;
                    let register = this.REGISTER;

                    if(this.isForOrganization){
                      const organization = {
                        name: company,
                        phone: phone,
                        braintree_subscription_id: paymentResponse.data.subscription.id,
                        user_admin_attributes: {
                          full_name: changeToInitialCaps(full_name),
                          phone_number: phone,
                          password: password,
                          password_confirmation: password,
                          email: email,
                          plan_id: this.planInformation.id,
                          braintree_customer_id: paymentResponse.data.subscription.transactions[0].customer.id,
                        },
                      };
                      register = this.REGISTER_ORG;
                      registrationInfo = { organization };
                    }else{
                      // Prepare data for user registration (sign up).
                      this.registrationInfo.full_name = changeToInitialCaps(full_name);
                      this.registrationInfo.email = email;
                      this.registrationInfo.password = password;
                      this.registrationInfo.last_braintree_transaction_id = paymentResponse.data.subscription.transactions[0].id;
                      this.registrationInfo.braintree_customer_id = paymentResponse.data.subscription.transactions[0].customer.id;
                      this.registrationInfo.plan_id = this.planInformation.id;
                      this.registrationInfo.braintree_subscription_id = paymentResponse.data.subscription.id;
                      this.registrationInfo.type = "Presenter";
                      registrationInfo = normalizeUserData(this.registrationInfo);
                    }

                    register(registrationInfo).then(response => {
                      // Successful signup, then login the user
                      let loginCredentials = {}
                      loginCredentials.email = email
                      loginCredentials.password = password
                      loginCredentials = normalizeUserData(loginCredentials)
                      // Call login service.
                      this.LOG_IN(loginCredentials).then(response => {
                        // If successful login, the state 'currentUser' will be set and user
                        // will be redirected to Dashboard.
                        // @see watch
                        resolve(true)
                      }, error => {
                        reject(error)
                      })
                    }, error => {
                      reject(error)
                    });
                    this.$emit("on-validate", this.registrationInfo, true);
                    resolve(true);
                  }
                })
                // Payment process failed.
                .catch((err) => {
                  this.SET_PAYMENT_API_ERRORS("Payment was unsuccessful, please check credit card details and try again.");
                  reject(err);
                });
            })
            // Braintree data tokenization failed.
            .catch((err) => {
              const fields = this.hostedFieldInstance._fields;
              if (!err.details) {
                Object.keys(fields).map(function (key, index) {
                  fields[key].containerElement.classList.add("is-invalid");
                });
              } else {
                const fieldsWithError = err.details.invalidFields;
                const fieldskeys = err.details.invalidFieldKeys;
                fieldskeys.map((key) => {
                  fieldsWithError[key].classList.add("is-invalid");
                });
              }
              // console.error("the braintree error", err);

              // todo: display validation message in the form if promise is rejected at this point
              this.braintreeError = "Invalid Payment Details. Please check and try again";
              reject("BRAINTREE TOKENIZE CALL ERRORS");
            });
        }
      });
    },
    formatCurrency: function(value){
      let formatter = currencyFormatter();
      return formatter.format(value);
    },
  },
  watch: {
    currentUser() {
      if (this.currentUser) this.$router.push({ name: "UserDashboard" });
    },
  },
  mounted() {
    // Initialize Braintree Hosted Fields.
    braintree.client
      .create({
        authorization: process.env.VUE_APP_BRAINTREE_API_KEY,
      })
      .then((clientInstance) => {
        let options = {
          client: clientInstance,
          styles: {
            input: {
              "font-size": "16px",
              "font-family": "'Lato', sans-serif",
              color: "#0344A9",
              padding: "0 0 0 15px",
            },
            ".invalid": {
              "border-color": "red",
            },
          },
          fields: {
            number: {
              selector: "#creditCardNumber",
            },
            cvv: {
              selector: "#cvvInput",
            },
            expirationDate: {
              selector: "#expireDate",
            },
            cardholderName: {
              selector: "#cardHolderName",
            },
          },
        };
        return braintree.hostedFields.create(options);
      })
      .then((hostedFieldInstance) => {
        this.hostedFieldInstance = hostedFieldInstance;
        hostedFieldInstance.on("empty", function (event) {
          const field = event.fields[event.emittedBy];
          field.container.classList.remove("notEmpty");
        });

        hostedFieldInstance.on("notEmpty", function (event) {
          const field = event.fields[event.emittedBy];
          field.container.classList.add("notEmpty");
        });

        hostedFieldInstance.on("validityChange", function (event) {
          const field = event.fields[event.emittedBy];
          if (field.isValid) {
            field.container.classList.remove("is-invalid");
          } else {
            field.container.classList.add("is-invalid");
          }
        });
      })
      .catch((err) => {});
  },
};
</script>

<style lang="less" scoped>
@import "../../assets/components/signUpForm/CreditCardForm.less";
@import "../../assets/components/common/FormInput.less";
</style>
